import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Bookmark, CreateBookmarkInput, UpdateBookmarkInput } from '@td/shared';
import { CLIENT_ID, get, post, patch, del, ApiRequestError } from '../lib/api';

const KEY = ['bookmarks'];

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: KEY });
  void qc.invalidateQueries({ queryKey: ['dashboard'] });
}

export function useBookmarks() {
  return useQuery({ queryKey: KEY, queryFn: () => get<Bookmark[]>('/bookmarks') });
}

export function useCreateBookmark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBookmarkInput) => post<Bookmark>('/bookmarks', input),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateBookmark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateBookmarkInput & { id: string }) =>
      patch<Bookmark>(`/bookmarks/${id}`, input),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteBookmark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<void>(`/bookmarks/${id}`),
    onSuccess: () => invalidate(qc),
  });
}

export function useReorderBookmarks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => post<Bookmark[]>('/bookmarks/reorder', { ids }),
    onSettled: () => invalidate(qc),
  });
}

export function useUploadBookmarkImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: Blob }) => {
      const form = new FormData();
      form.append('image', file, 'icon.webp');
      const res = await fetch(`/api/bookmarks/${id}/image`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Client-Id': CLIENT_ID },
        body: form,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new ApiRequestError(res.status, body.error ?? 'Upload failed');
      }
      return (await res.json()) as Bookmark;
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteBookmarkImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<Bookmark>(`/bookmarks/${id}/image`),
    onSuccess: () => invalidate(qc),
  });
}
